// Portfolio Administration v1.0 PR 5 — cross-portfolio Vendor registry
// detail page at /portfolio-admin/vendors/:vendorId. Renders an Entity
// Detail Page Layout v1.1 §3 strip of 18 tabs: 11 module tabs (Overview /
// Portal account / Portal review / Portal notifications / Qualifications /
// Contract terms / Assignments / Reports / Annual audit / VFI ledger /
// Performance) plus the 7 cross-cutting ones (Documents / Tasks / Calendar /
// Messages / Communications / Notes / Activity). TABS below is the source of
// truth — this header has gone stale before, so count it against TABS.
//
// The header used to say "canonical 12-tab", counting six module tabs and
// six cross-cutting ones — a shape this page last had several PRs ago —
// and described VFI ledger and Performance as explanatory empty states.
// Both read real data now. The placeholder "Cross-loan agreements" tab is
// gone for good (the VendorAgreement entity never shipped; agreements live
// in the vendor's DMS folders).

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Award,
  BadgeCheck,
  Bell,
  Briefcase,
  ClipboardList,
  Coins,
  DollarSign,
  Inbox,
  KeyRound,
  Scale,
  Star,
} from "lucide-react";
import { Card } from "@/design-system/components/ui";
import {
  CROSS_CUTTING_TAB_ICONS,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useVendor } from "@/hooks/portfolioAdmin/useRegistries";
import { PlaceholderPage } from "@/pages/core/PlaceholderPage";
import {
  RegistryDetailLayout,
  useRegistryTabState,
} from "@/components/portfolioAdmin/registry/RegistryDetailLayout";
import { VendorDetailHeader } from "@/components/portfolioAdmin/vendors/VendorDetailHeader";
import { VendorOverviewTab } from "@/components/portfolioAdmin/vendors/tabs/VendorOverviewTab";
import { VendorContractTermsTab } from "@/components/portfolioAdmin/vendors/tabs/VendorContractTermsTab";
import { VendorQualificationsTab } from "@/components/portfolioAdmin/vendors/tabs/VendorQualificationsTab";
import { VendorAssignmentsTab } from "@/components/portfolioAdmin/vendors/tabs/VendorAssignmentsTab";
import { VendorVfiLedgerTab } from "@/components/portfolioAdmin/vendors/tabs/VendorVfiLedgerTab";
import { VendorPerformanceTab } from "@/components/portfolioAdmin/vendors/tabs/VendorPerformanceTab";
import { VendorReportsTab } from "@/components/portfolioAdmin/vendors/tabs/VendorReportsTab";
import { VendorAnnualAuditTab } from "@/components/portfolioAdmin/vendors/tabs/VendorAnnualAuditTab";
import { VendorPortalAccountTab } from "@/components/portfolioAdmin/vendors/tabs/VendorPortalAccountTab";
import { VendorPortalReviewTab } from "@/components/portfolioAdmin/vendors/tabs/VendorPortalReviewTab";
import { VendorPortalNotificationsTab } from "@/components/portfolioAdmin/vendors/tabs/VendorPortalNotificationsTab";

export type VendorTabId =
  | "overview"
  | "portal-account"
  | "portal-review"
  | "portal-notifications"
  | "qualifications"
  | "contract-terms"
  | "assignments"
  | "reports"
  | "annual-audit"
  | "vfi-ledger"
  | "performance"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly VendorTabId[] = [
  "overview",
  "portal-account",
  "portal-review",
  "portal-notifications",
  "qualifications",
  "contract-terms",
  "assignments",
  "reports",
  "annual-audit",
  "vfi-ledger",
  "performance",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<VendorTabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  // Party-surface standard (§2.1) — the three portal tabs sit directly
  // after Overview with the PartyDetailPage labels/icons. Portal account
  // hosts the shared portal-absorption tab (the card moved off Overview);
  // Review queue & requests is the COLLECTION staging queue (the vendor
  // monthly-report review flow stays on the Reports tab below).
  { key: "portal-account", label: "Portal account", icon: KeyRound },
  { key: "portal-review", label: "Review queue & requests", icon: Inbox },
  { key: "portal-notifications", label: "Notifications", icon: Bell },
  { key: "qualifications", label: "Qualifications", icon: BadgeCheck },
  { key: "contract-terms", label: "Contract Terms", icon: Coins },
  { key: "assignments", label: "Assignments", icon: Briefcase },
  { key: "reports", label: "Reports", icon: ClipboardList },
  // The strip is ICON-ONLY at every viewport (RegistryDetailLayout never
  // passes showLabels), so this label reaches the reader only through the
  // tooltip and the aria-label — hence a balance scale, which reads as
  // "weighed against the market" rather than as generic paperwork.
  { key: "annual-audit", label: "Annual audit", icon: Scale },
  { key: "vfi-ledger", label: "VFI Ledger", icon: DollarSign },
  { key: "performance", label: "Performance", icon: Star },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
];

// Keep `Award` imported as a future-proof icon for the deferred
// performance-cadence visualization without warning in eslint.
void Award;

export default function VendorDetailPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <PlaceholderPage
        phase="Phase 3"
        title="Vendors"
        description="Required vendors, VFI ledger, MNCA, VVCA."
      />
    );
  }
  return <VendorDetailPageInner />;
}

function VendorDetailPageInner() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useRegistryTabState<VendorTabId>(TABS, params, setParams);

  const vendorQuery = useVendor(vendorId);
  const vendor = vendorQuery.data;

  if (vendorQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading vendor…</p>
      </Card>
    );
  }

  if (!vendor) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Vendor not found</h2>
        <p className="text-small text-gray-mid">
          The vendor you requested was not found or you don&apos;t have
          permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/portfolio-admin/vendors")}
        >
          Back to Vendors
        </button>
      </Card>
    );
  }

  return (
    <RegistryDetailLayout
      testIdPrefix="vendor"
      tabsAriaLabel="Vendor sections"
      header={<VendorDetailHeader vendor={vendor} />}
      tabItems={TAB_ITEMS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "overview" && <VendorOverviewTab vendor={vendor} />}
      {tab === "portal-account" && <VendorPortalAccountTab vendor={vendor} />}
      {tab === "qualifications" && <VendorQualificationsTab />}
      {tab === "contract-terms" && <VendorContractTermsTab vendor={vendor} />}
      {tab === "assignments" && <VendorAssignmentsTab vendorId={vendor.id} />}
      {tab === "reports" && <VendorReportsTab vendor={vendor} />}
      {tab === "annual-audit" && <VendorAnnualAuditTab vendor={vendor} />}
      {tab === "portal-review" && <VendorPortalReviewTab vendor={vendor} />}
      {tab === "portal-notifications" && <VendorPortalNotificationsTab vendor={vendor} />}
      {tab === "vfi-ledger" && <VendorVfiLedgerTab vendor={vendor} />}
      {tab === "performance" && <VendorPerformanceTab vendor={vendor} />}
      {tab === "documents" && (
        <DocumentPanel ownerType="Vendor" ownerId={vendor.id} />
      )}
      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="Vendor"
          relatedEntityId={vendor.id}
          addButtonLabel="+ Add task linked to this Vendor"
        />
      )}
      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="Vendor"
          relatedEntityId={vendor.id}
          entityLabel={vendor.name}
          entityArchived={vendor.isArchived}
        />
      )}
      {tab === "messages" && (
        <RelatedConversationsPanel
          resourceType="vendor"
          resourceId={vendor.id}
          entityLabel={vendor.name}
        />
      )}
      {tab === "communications" && (
        <CommunicationsTabPanel ownerType="vendor" ownerId={vendor.id} ownerLabel={vendor.name} />
      )}
      {tab === "notes" && (
        <NotesTabPanel ownerType="vendor" ownerId={vendor.id} />
      )}
      {tab === "activity" && (
        <ActivityTabPanel ownerType="vendor" ownerId={vendor.id} />
      )}
    </RegistryDetailLayout>
  );
}
